import { useState, useEffect, useCallback } from 'react';
import Modal from '../../BaseComponents/Modal';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import { ConsentValue, ConsentChangeLog, CommunicationConsentProps } from './types';
import { getMemberConsent, updateMemberConsent, getMemberConsentHistory, ConsentData } from '../../../api/consent';
import './CommunicationConsent.scss';
import { formatUKDate } from '../../../utils';

const CommunicationConsent = ({ memberId, onConsentChange, disclaimerViewedAt }: CommunicationConsentProps) => {
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isChangeLogModalOpen, setIsChangeLogModalOpen] = useState(false);
  const [consentData, setConsentData] = useState<ConsentData | null>(null);
  const [changeLogData, setChangeLogData] = useState<ConsentChangeLog[]>([]);
  const [localConsentData, setLocalConsentData] = useState<ConsentData | null>(null);
  const [isLoadingConsent, setIsLoadingConsent] = useState(false);
  const [isLoadingChangeLog, setIsLoadingChangeLog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConsentData = useCallback(async () => {
    try {
      setIsLoadingConsent(true);
      setError(null);
      const data = await getMemberConsent(memberId);
      setConsentData(data);
      setLocalConsentData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load consent data');
    } finally {
      setIsLoadingConsent(false);
    }
  }, [memberId]);

  const fetchChangeLog = useCallback(async () => {
    try {
      setIsLoadingChangeLog(true);
      setError(null);
      const response = await getMemberConsentHistory(memberId);
      
      const toBoolean = (value: any): boolean => {
        if (typeof value === 'boolean') return value;
        if (value === null || value === undefined) return false;
        const normalized = String(value).trim().toLowerCase();
        return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'enable' || normalized === 'enabled';
      };

      const hiddenFields = [
        'communication_preferences.phone_calls',
        'content_types.health_education',
        'content_types.programme_invites',
        'content_types.feedback_surveys'
      ];

      const transformedLogs: ConsentChangeLog[] = response.history
        .filter((item) => !hiddenFields.includes(item.field_name))
        .map((item) => {
          let formattedField = item.field_name;
          if (item.field_name.startsWith('communication_preferences.')) {
            const field = item.field_name.replace('communication_preferences.', '');
            formattedField = field.charAt(0).toUpperCase() + field.slice(1);
          } else if (item.field_name.startsWith('content_types.')) {
            const field = item.field_name.replace('content_types.', '');
            formattedField = field.split('_').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ');
          }
          
          const isPrevEmpty = item.previous_value === '' || item.previous_value === null || item.previous_value === undefined;
          
          return {
            id: item.id,
            date: new Date(item.changed_at).toLocaleString(),
            changedBy: 'System',
            field: formattedField,
            oldValue: isPrevEmpty ? 'Empty' : (toBoolean(item.previous_value) ? 'Enable' : 'Disable'),
            newValue: toBoolean(item.new_value) ? 'Enable' : 'Disable'
          };
        });
      
      setChangeLogData(transformedLogs);
    } catch (err: any) {
      setError(err.message || 'Failed to load change log');
    } finally {
      setIsLoadingChangeLog(false);
    }
  }, [memberId]);

  useEffect(() => {
    if (isConsentModalOpen) {
      fetchConsentData();
    }
  }, [isConsentModalOpen, fetchConsentData]);

  useEffect(() => {
    if (isChangeLogModalOpen) {
      fetchChangeLog();
    }
  }, [isChangeLogModalOpen, fetchChangeLog]);

  const handleConsentButtonClick = () => {
    setIsConsentModalOpen(true);
  };

  const handleChangeLogClick = async () => {
    try {
      setIsUpdating(true);
      setError(null);
      
      if (hasUnsavedChanges()) {
        const updateData = {
          member_id: memberId,
          communication_preferences: localConsentData!.communication_preferences,
          content_types: localConsentData!.content_types
        };
        
        const updatedData = await updateMemberConsent(memberId, updateData);
        setConsentData(updatedData);
        setLocalConsentData(updatedData);
        
        if (onConsentChange) {
          onConsentChange(updatedData);
        }
        
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      await fetchChangeLog();
      setIsConsentModalOpen(false);
      setIsChangeLogModalOpen(true);
      
    } catch (err: any) {
      setError(err.message || 'Failed to update consent settings');
      await fetchConsentData();
    } finally {
      setIsUpdating(false);
    }
  };

const closeConsentModal = () => {
    setIsConsentModalOpen(false);
    setError(null);
    if (consentData) {
      setLocalConsentData(consentData);
    }
  };
  const closeChangeLogModal = () => {
    setIsChangeLogModalOpen(false);
    setError(null);
  };
  const handleConsentUpdate = (field: string, value: boolean) => {
    if (!localConsentData || !localConsentData.communication_preferences || !localConsentData.content_types) return;
    const updatedData = { ...localConsentData };
    if (field.startsWith('comm_')) {
      const prefKey = field.replace('comm_', '') as keyof typeof updatedData.communication_preferences;
      updatedData.communication_preferences = {
        ...updatedData.communication_preferences,
        [prefKey]: value
      };
    } else if (field.startsWith('content_')) {
      const contentKey = field.replace('content_', '') as keyof typeof updatedData.content_types;
      updatedData.content_types = {
        ...updatedData.content_types,
        [contentKey]: value
      };
    }
    setLocalConsentData(updatedData);
  };
  const getCommunicationPreferences = (): ConsentValue[] => {
    if (!consentData || !consentData.communication_preferences) return [];
    return [
      {
        id: 'comm_email',
        label: 'Email',
        isActive: consentData.communication_preferences.email || false
      },
      {
        id: 'comm_sms',
        label: 'SMS',
        isActive: consentData.communication_preferences.sms || false
      },
      {
        id: 'comm_whatsapp',
        label: 'WhatsApp',
        isActive: consentData.communication_preferences.whatsapp || false
      }
    ];
  };
  const getContentTypes = (): ConsentValue[] => {
    if (!consentData || !consentData.content_types) return [];
    return [
      {
        id: 'content_appointment_reminders',
        label: 'Appointment Reminders',
        isActive: consentData.content_types.appointment_reminders || false
      }
    ];
  };

  const hasUnsavedChanges = () => {
    if (!consentData || !localConsentData || 
        !consentData.communication_preferences || !consentData.content_types ||
        !localConsentData.communication_preferences || !localConsentData.content_types) return false;
    
    return (
      JSON.stringify(consentData.communication_preferences) !== JSON.stringify(localConsentData.communication_preferences) ||
      JSON.stringify(consentData.content_types) !== JSON.stringify(localConsentData.content_types)
    );
  };

  const isLoading = isLoadingConsent;

  if (!memberId) {
    return (
      <div className="communication-consent-btn">
        <Button
          text="Consent"
          onClick={() => {}}
          color="white"
          borderRadius="8px"
          paddingTop="12px"
          paddingRight="24px"
          paddingBottom="12px"
          paddingLeft="24px"
          width="100%"
          isDisabled={true}
        />
      </div>
    );
  }

  return (
    <>
      <div className="communication-consent-btn">
        <Button
          text="Consent"
          onClick={handleConsentButtonClick}
          color="white"
          borderRadius="8px"
          paddingTop="12px"
          paddingRight="24px"
          paddingBottom="12px"
          paddingLeft="24px"
          width="100%"
        />
      </div>

      <Modal
        isOpen={isConsentModalOpen}
        onClose={closeConsentModal}
        title="Consent"
        width="600px"
      >
        <div className="consent-modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <div className="intro-text">
            This section displays the member's currently active communication preferences and content types.
            <div style={{ marginTop: 8 }}>
              <strong>Disclaimer viewed at:</strong>{' '}
              {disclaimerViewedAt
                ? formatUKDate(disclaimerViewedAt, 'dd/MM/yyyy hh:mm a')
                : 'Not recorded'}
            </div>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <Loader />
            </div>
          ) : (
            <>
              <div className="consent-section">
                <h3 className="section-title">Communication Preferences</h3>
                <div className="consent-list">
                  {consentData ? (
                    getCommunicationPreferences().map((preference) => (
                      <div key={preference.id} className="consent-item">
                        <span className="consent-label">{preference.label}</span>
                        <input
                          type="checkbox"
                          checked={preference.isActive}
                          onChange={() => handleConsentUpdate(preference.id, !preference.isActive)}
                          className="consent-checkbox"
                          disabled={isUpdating}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="no-data-message">
                      No consent data available.
                    </div>
                  )}
                </div>
              </div>

              <div className="consent-section">
                <h3 className="section-title">Content Types</h3>
                <div className="consent-list">
                  {consentData ? (
                    getContentTypes().map((content) => (
                      <div key={content.id} className="consent-item">
                        <span className="consent-label">{content.label}</span>
                        <input
                          type="checkbox"
                          checked={content.isActive}
                          onChange={() => handleConsentUpdate(content.id, !content.isActive)}
                          className="consent-checkbox"
                          disabled={isUpdating}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="no-data-message">
                      No content types available.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          
          <div className="modal-footer">
            <Button
              text="Change Log"
              onClick={handleChangeLogClick}
              backgroundColor="#374151"
              color="white"
              borderRadius="8px"
              paddingTop="8px"
              paddingRight="16px"
              paddingBottom="8px"
              paddingLeft="16px"
              isDisabled={isLoading || isUpdating}
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isChangeLogModalOpen}
        onClose={closeChangeLogModal}
        title="Change History"
        width="800px"
      >
        <div className="change-log-modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {isLoadingChangeLog ? (
            <div className="loading-container">
              <Loader />
              <p>Loading change history...</p>
            </div>
          ) : (
            <>
              {changeLogData.length > 0 ? (
                <div className="change-log-table">
                  <div className="table-header">
                    <div className="header-cell">Date/Time</div>
                    <div className="header-cell">Field</div>
                    <div className="header-cell">Previous Value</div>
                    <div className="header-cell">New Value</div>
                  </div>
                  <div className="table-body">
                    {changeLogData.map((log) => (
                      <div key={log.id} className="table-row">
                        <div className="table-cell">{log.date}</div>
                        <div className="table-cell">{log.field}</div>
                        <div className="table-cell">{log.oldValue}</div>
                        <div className="table-cell">{log.newValue}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-data-message">
                  <p>No change history found for this member.</p>
                  <p>This could mean:</p>
                  <ul>
                    <li>No consent changes have been made yet</li>
                    <li>The member is new and hasn't set preferences</li>
                    <li>There was an issue loading the data</li>
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default CommunicationConsent;